'use client'

import { type FormEvent, useEffect, useMemo, useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Switch } from '@/components/ui/switch'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { Textarea } from '@/components/ui/textarea'
import { useI18n } from '@/lib/hooks/use-i18n'
import { useToast } from '@/lib/hooks/use-toast'
import { matrixApiClient } from '@/lib/api/matrix-client'
import { MATRIX_API } from '@/api/MATRIX_API'
import { Mode } from '@/lib/types/digital-employee'

type ApiEnvelope<T> = { code?: number; debug_msg?: string; msg?: string; data?: T } & T

type DeviceArea = {
  id: string
  name: string
}

type DeviceDetail = {
  area_id?: string
  available?: boolean
  forward_port?: number
  id: string
  mode?: number
  remark?: string
  serial_number?: string
  tags?: string
}

type CreateFormState = {
  areaId: string
  available: boolean
  forwardPort: string
  mode: string
  serialNumber: string
  remark: string
  tags: string
}

const createEmptyFormState = (): CreateFormState => ({
  areaId: '',
  available: true,
  forwardPort: '0',
  mode: String(Mode.Otg),
  serialNumber: '',
  remark: '',
  tags: '',
})

type CreateDigitalEmployeeDialogProps = {
  open: boolean
  onOpenChange: (open: boolean) => void
  deviceId?: string
}

const unwrapMatrixResponse = <T,>(payload: ApiEnvelope<T>): T => {
  if (payload && typeof payload === 'object' && 'code' in payload) {
    const code = (payload as { code?: number }).code
    if (code) {
      const message =
        (payload as { debug_msg?: string; msg?: string }).debug_msg ||
        (payload as { debug_msg?: string; msg?: string }).msg ||
        `Request failed (code=${String(code)})`
      throw new Error(message)
    }
    if ('data' in payload) return (payload as { data: T }).data
  }
  return payload as T
}

export function CreateDigitalEmployeeDialog({
  open,
  onOpenChange,
  deviceId,
}: CreateDigitalEmployeeDialogProps) {
  const { t } = useI18n()
  const { toast } = useToast()
  const queryClient = useQueryClient()
  const [formState, setFormState] = useState<CreateFormState>(createEmptyFormState)
  const isEditMode = Boolean(deviceId)

  const { data: deviceAreas = [], isLoading: isAreasLoading } = useQuery({
    queryKey: ['matrix-device-admin-areas'],
    queryFn: async () => {
      const response = await matrixApiClient.post<ApiEnvelope<{ list?: DeviceArea[] }>>(
        MATRIX_API.deviceAdmin.listArea,
        { page: 1, page_size: 200 }
      )
      const data = unwrapMatrixResponse(response)
      return Array.isArray(data) ? data : data.list ?? []
    },
    enabled: open,
    staleTime: 5 * 60 * 1000,
  })

  const { data: deviceDetail, isLoading: isDetailLoading } = useQuery({
    queryKey: ['matrix-device-admin-detail', deviceId],
    queryFn: async () => {
      const response = await matrixApiClient.post<ApiEnvelope<DeviceDetail>>(
        MATRIX_API.deviceAdmin.get,
        { id: deviceId }
      )
      return unwrapMatrixResponse(response)
    },
    enabled: open && Boolean(deviceId),
  })

  useEffect(() => {
    if (!open) return
    if (!deviceId) {
      setFormState(createEmptyFormState)
      return
    }
    if (!deviceDetail) {
      setFormState(createEmptyFormState)
      return
    }
    setFormState({
      areaId: deviceDetail.area_id ?? '',
      available: deviceDetail.available ?? true,
      forwardPort:
        deviceDetail.forward_port !== undefined ? String(deviceDetail.forward_port) : '0',
      mode: deviceDetail.mode !== undefined ? String(deviceDetail.mode) : String(Mode.Otg),
      serialNumber: deviceDetail.serial_number ?? '',
      remark: deviceDetail.remark ?? '',
      tags: deviceDetail.tags ?? '',
    })
  }, [deviceDetail, deviceId, open])

  const createMutation = useMutation({
    mutationFn: async (payload: {
      area_id: string
      available: boolean
      forward_port: number
      mode: number
      remark: string
      serial_number: string
      tags: string
    }) => {
      const response = await matrixApiClient.post<ApiEnvelope<string>>(
        MATRIX_API.deviceAdmin.create,
        payload
      )
      return unwrapMatrixResponse(response)
    },
    onSuccess: () => {
      toast({ title: t('digital_employees.toast.create_success') })
      queryClient.invalidateQueries({
        queryKey: ['digital-employees'],
        exact: false,
      })
      setFormState(createEmptyFormState)
      onOpenChange(false)
    },
    onError: (error) => {
      toast({
        title: t('digital_employees.toast.create_error'),
        description: error instanceof Error ? error.message : undefined,
        variant: 'destructive',
      })
    },
  })

  const updateMutation = useMutation({
    mutationFn: async (payload: {
      area_id: string
      available: boolean
      forward_port: number
      id: string
      mode: number
      remark: string
      serial_number: string
      tags: string
    }) => {
      const response = await matrixApiClient.post<ApiEnvelope<string>>(
        MATRIX_API.deviceAdmin.update,
        payload
      )
      return unwrapMatrixResponse(response)
    },
    onSuccess: () => {
      toast({ title: t('digital_employees.toast.update_success') })
      queryClient.invalidateQueries({
        queryKey: ['digital-employees'],
        exact: false,
      })
      setFormState(createEmptyFormState)
      onOpenChange(false)
    },
    onError: (error) => {
      toast({
        title: t('digital_employees.toast.update_error'),
        description: error instanceof Error ? error.message : undefined,
        variant: 'destructive',
      })
    },
  })

  const handleSubmit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault()
    const forwardPort = Number.parseInt(formState.forwardPort, 10)
    const normalizedForwardPort = Number.isFinite(forwardPort) ? forwardPort : 0
    const mode = Number.parseInt(formState.mode, 10)
    const normalizedMode = Number.isFinite(mode) ? mode : 0
    const payload = {
      area_id: formState.areaId,
      available: formState.available,
      forward_port: normalizedForwardPort,
      mode: normalizedMode,
      serial_number: formState.serialNumber.trim(),
      remark: formState.remark.trim(),
      tags: formState.tags.trim(),
    }

    if (deviceId) {
      updateMutation.mutate({ ...payload, id: deviceId })
    } else {
      createMutation.mutate(payload)
    }
  }

  const isSubmitting = createMutation.isPending || updateMutation.isPending
  const isFormDisabled = isSubmitting || isDetailLoading
  const isSubmitDisabled = !formState.serialNumber.trim() || !formState.areaId || isFormDisabled

  const dialogTitle = useMemo(() => {
    return isEditMode
      ? t('digital_employees.dialog.title_edit')
      : t('digital_employees.dialog.title')
  }, [isEditMode, t])
  const submitLabel = useMemo(() => {
    return isEditMode
      ? t('digital_employees.dialog.submit_update')
      : t('digital_employees.dialog.submit')
  }, [isEditMode, t])
  const submittingLabel = useMemo(() => {
    return isEditMode
      ? t('digital_employees.dialog.updating')
      : t('digital_employees.dialog.creating')
  }, [isEditMode, t])

  const handleDialogOpenChange = (value: boolean) => {
    if (!value) {
      setFormState(createEmptyFormState)
    }
    onOpenChange(value)
  }

  const showForwardPort = formState.mode === String(Mode.Usb)

  return (
    <Dialog open={open} onOpenChange={handleDialogOpenChange}>
      <DialogContent className="sm:max-w-[640px]">
        <DialogHeader>
          <DialogTitle>{dialogTitle}</DialogTitle>
          <DialogDescription>{t('digital_employees.dialog.description')}</DialogDescription>
        </DialogHeader>
        <form className="space-y-4" onSubmit={handleSubmit}>
          <div className="grid gap-1">
            <Label htmlFor="digital-employee-serial-number">
              {t('digital_employees.dialog.fields.serial_number')}
            </Label>
            <Input
              id="digital-employee-serial-number"
              value={formState.serialNumber}
              onChange={(event) =>
                setFormState((prev) => ({ ...prev, serialNumber: event.target.value }))
              }
              autoComplete="off"
              disabled={isFormDisabled}
            />
          </div>

          <div className="grid gap-1">
            <Label htmlFor="digital-employee-area">
              {t('digital_employees.dialog.fields.area')}
            </Label>
            <Select
              value={formState.areaId}
              onValueChange={(value) => setFormState((prev) => ({ ...prev, areaId: value }))}
              disabled={isFormDisabled || isAreasLoading}
            >
              <SelectTrigger id="digital-employee-area" className="cursor-pointer">
                <SelectValue placeholder={t('digital_employees.dialog.fields.area_placeholder')} />
              </SelectTrigger>
              <SelectContent>
                {deviceAreas.map((area) => (
                  <SelectItem key={area.id} value={area.id}>
                    {area.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="grid gap-1">
            <Label htmlFor="digital-employee-device-mode">
              {t('digital_employees.dialog.fields.device_mode')}
            </Label>
            <Select
              value={formState.mode}
              onValueChange={(value) => setFormState((prev) => ({ ...prev, mode: value }))}
              disabled={isFormDisabled}
            >
              <SelectTrigger id="digital-employee-device-mode" className="cursor-pointer">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value={String(Mode.Otg)}>
                  {t('digital_employees.dialog.fields.device_mode_otg')}
                </SelectItem>
                <SelectItem value={String(Mode.Usb)}>
                  {t('digital_employees.dialog.fields.device_mode_usb')}
                </SelectItem>
              </SelectContent>
            </Select>
          </div>

          {showForwardPort ? (
            <div className="grid gap-1">
              <Label htmlFor="digital-employee-forward-port">
                {t('digital_employees.dialog.fields.forward_port')}
              </Label>
              <Input
                id="digital-employee-forward-port"
                type="number"
                min={0}
                value={formState.forwardPort}
                onChange={(event) =>
                  setFormState((prev) => ({ ...prev, forwardPort: event.target.value }))
                }
                autoComplete="off"
                disabled={isFormDisabled}
              />
            </div>
          ) : null}

          <div className="grid gap-1">
            <Label htmlFor="digital-employee-available">
              {t('digital_employees.dialog.fields.available')}
            </Label>
            <div className="flex items-center gap-3">
              <Switch
                id="digital-employee-available"
                checked={formState.available}
                onCheckedChange={(value) =>
                  setFormState((prev) => ({ ...prev, available: Boolean(value) }))
                }
                disabled={isFormDisabled}
              />
              <span className={`text-sm ${formState.available ? 'text-green-600' : 'text-muted-foreground'}`}>
                {formState.available
                  ? t('digital_employees.dialog.fields.available_on')
                  : t('digital_employees.dialog.fields.available_off')}
              </span>
            </div>
          </div>

          <div className="grid gap-1">
            <Label htmlFor="digital-employee-tags">
              {t('digital_employees.dialog.fields.tags')}
            </Label>
            <Input
              id="digital-employee-tags"
              value={formState.tags}
              onChange={(event) =>
                setFormState((prev) => ({ ...prev, tags: event.target.value }))
              }
              placeholder={t('digital_employees.dialog.fields.tags_placeholder')}
              autoComplete="off"
              disabled={isFormDisabled}
            />
          </div>

          <div className="grid gap-1">
            <Label htmlFor="digital-employee-remark">
              {t('digital_employees.dialog.fields.remark')}
            </Label>
            <Textarea
              id="digital-employee-remark"
              rows={3}
              value={formState.remark}
              onChange={(event) =>
                setFormState((prev) => ({ ...prev, remark: event.target.value }))
              }
              disabled={isFormDisabled}
            />
          </div>

          <DialogFooter className="gap-2">
            <Button
              type="button"
              variant="ghost"
              className="cursor-pointer"
              onClick={() => onOpenChange(false)}
            >
              {t('digital_employees.dialog.cancel')}
            </Button>
            <Button type="submit" className="cursor-pointer" disabled={isSubmitDisabled}>
              {isSubmitting ? submittingLabel : submitLabel}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  )
}
